import { useRef, useState } from "react";

import { SectionConfig } from "../../DashboardHeader/DashboardParams";

/**
 * Drag-to-reorder interaction state for whole dashboard SECTIONS, the
 * section-level sibling of `usePlacedReorder` (cards). Same ownership split:
 * this hook holds only the transient drag UI (which section is being dragged,
 * which GAP the blue divider marks) and the native HTML5 drag-and-drop
 * wiring. It does NOT own the section order — on drop it calls
 * `onDrop(fromId, dropIndex)`, so the URL-backed section list stays the
 * single source of truth.
 *
 * The drop target is a GAP INDEX over the original sections array:
 * `dropIndex ∈ [0, count]`, where the section at array index `i` is adjacent
 * to gaps `i` (before it) and `i + 1` (after it). Hovering a section resolves
 * the gap from the pointer's position within it ({@link gapIndexFor}) on the
 * axis the sections flow along — vertical when they stack full-width,
 * horizontal when several share a row. `dropIndex` drives the divider that
 * previews where the dragged section would land.
 *
 * Section drags stamp a custom dataTransfer type
 * ({@link SECTION_DRAG_TYPE}), and every drop handler ignores gestures
 * without it — so card drags (and OS file drags) can never move the divider
 * or reorder sections, independent of React state timing.
 *
 * Releasing over an ACCEPTING surface that has no drop handler of its own
 * (only `dragend` fires — no `drop`; e.g. the sections-grid gaps, which the
 * consumer accepts at container level) still commits at the last `dropIndex`
 * the divider was showing — the same commit-what-the-preview-promised
 * contract as `usePlacedReorder`. An in-place landing no-ops in the caller
 * via {@link reorderSections}. A CANCELLED drag — Escape, or a release
 * nothing accepted — reports `dropEffect === "none"` at dragend, and the
 * pending reorder is discarded.
 */

/** Custom dataTransfer type stamped on section drags. `types` (not values) is
 *  readable during dragover, so targets can discriminate section drags from
 *  card drags in protected mode. */
export const SECTION_DRAG_TYPE = "application/x-roboto-section";

/** The axis sections flow along at the current width: "y" when stacked
 *  full-width (divider is horizontal), "x" when side-by-side (vertical). */
export type SectionAxis = "x" | "y";

/**
 * The gap index a pointer over the section at array index `index` selects:
 * `index` (insert before) when the pointer is in the leading half of `rect`
 * along `axis`, else `index + 1` (insert after). The exact midpoint counts as
 * the trailing half.
 */
export function gapIndexFor(
  index: number,
  axis: SectionAxis,
  pointer: { x: number; y: number },
  rect: { left: number; top: number; width: number; height: number },
): number {
  const before =
    axis === "y"
      ? pointer.y < rect.top + rect.height / 2
      : pointer.x < rect.left + rect.width / 2;
  return before ? index : index + 1;
}

/**
 * Reorder `sections` so the section `fromId` occupies gap `dropIndex` of the
 * ORIGINAL array. Returns null when the move is a no-op — the drop lands
 * where the section already is (`dropIndex` is `fromIndex` or
 * `fromIndex + 1`) — or when `fromId` isn't in the list, so callers can skip
 * the layout write (and its dirty flag) entirely.
 */
export function reorderSections(
  sections: readonly SectionConfig[],
  fromId: string,
  dropIndex: number,
): SectionConfig[] | null {
  const fromIndex = sections.findIndex((s) => s.id === fromId);
  if (
    fromIndex === -1 ||
    dropIndex === fromIndex ||
    dropIndex === fromIndex + 1
  ) {
    return null;
  }
  const next = [...sections];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(dropIndex > fromIndex ? dropIndex - 1 : dropIndex, 0, moved);
  return next;
}

export interface SectionDragHandlers {
  draggable: true;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
}

export interface SectionDropHandlers {
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export interface SectionReorder {
  /** id of the section currently being dragged, or null. */
  draggingSectionId: string | null;
  /** gap index the divider marks (0..count), or null when not hovering. */
  dropIndex: number | null;
  /** Props making the element for `id` a section drag SOURCE. The caller owns
   *  target filtering (e.g. bail when the gesture started on a card). */
  dragHandlers: (id: string) => SectionDragHandlers;
  /** Props making one section wrapper (array index `index`, halved along
   *  `axis`) — or, with `fixedGap`, the trailing "Add section" slot — a drop
   *  TARGET. */
  dropHandlers: (
    index: number,
    axis: SectionAxis,
    opts?: { fixedGap?: number },
  ) => SectionDropHandlers;
}

/** True when the active drag carries the section discriminator type. */
function isSectionDrag(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes(SECTION_DRAG_TYPE);
}

export function useSectionReorder(
  onDrop: (fromId: string, dropIndex: number) => void,
): SectionReorder {
  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(
    null,
  );
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  // Whether a real `drop` committed the current gesture — guards the dragend
  // fallback against double-committing (dragend always follows drop).
  const dropHandledRef = useRef(false);
  // Generation counter for the rAF-deferred dragstart state update below: a
  // drag that ends before its deferred frame runs (instant press-release)
  // must not resurrect the dragging state after `clear` — bumping the
  // generation in `clear` invalidates any still-pending callback.
  const gestureRef = useRef(0);

  function clear() {
    gestureRef.current += 1;
    setDraggingSectionId(null);
    setDropIndex(null);
  }

  function dragHandlers(id: string): SectionDragHandlers {
    return {
      draggable: true,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = "move";
        // Some browsers require data for the drag to initiate; the custom
        // type is the section-drag discriminator drop targets key off.
        e.dataTransfer.setData("text/plain", id);
        e.dataTransfer.setData(SECTION_DRAG_TYPE, id);
        // Drag image: the section's header — already in-document (a
        // setDragImage requirement), compact, and it names the section. The
        // default ghost would rasterize the WHOLE section, which can be huge.
        const header = (e.currentTarget as HTMLElement).querySelector("header");
        if (header) {
          const rect = header.getBoundingClientRect();
          e.dataTransfer.setDragImage(
            header,
            e.clientX - rect.left,
            e.clientY - rect.top,
          );
        }
        // Defer the state update to the next frame. Changing the DOM
        // synchronously inside `dragstart` (the divider mounts, drag classes
        // apply) can detach the drag source and CANCEL the native drag — same
        // deferral as usePlacedReorder.
        dropHandledRef.current = false;
        const gesture = (gestureRef.current += 1);
        requestAnimationFrame(() => {
          if (gestureRef.current !== gesture) {
            // The drag already ended (dragend ran `clear` before this frame).
            return;
          }
          setDraggingSectionId(id);
        });
      },
      onDragEnd: (e) => {
        // dropEffect "none" means the drag was CANCELLED — Escape, or a
        // release nothing accepted — so the pending reorder is discarded.
        // Otherwise, released without a `drop` firing (e.g. in a sections-
        // grid gap the container accepted) while the divider was showing at
        // the last hovered gap: commit there — the drop the divider
        // promised. In-place landings no-op in the caller.
        const cancelled = e.dataTransfer.dropEffect === "none";
        if (!cancelled && !dropHandledRef.current && dropIndex !== null) {
          onDrop(id, dropIndex);
        }
        clear();
      },
    };
  }

  function dropHandlers(
    index: number,
    axis: SectionAxis,
    opts?: { fixedGap?: number },
  ): SectionDropHandlers {
    const gapAt = (e: React.DragEvent): number =>
      opts?.fixedGap ??
      gapIndexFor(
        index,
        axis,
        { x: e.clientX, y: e.clientY },
        e.currentTarget.getBoundingClientRect(),
      );
    return {
      onDragEnter: (e) => {
        if (!isSectionDrag(e)) {
          return;
        }
        e.preventDefault();
        setDropIndex(gapAt(e));
      },
      onDragOver: (e) => {
        if (!isSectionDrag(e)) {
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        // Recompute on every dragover so the divider flips as the pointer
        // crosses the section's midpoint (setState with an equal value is a
        // React no-op).
        setDropIndex(gapAt(e));
      },
      onDrop: (e) => {
        if (!isSectionDrag(e)) {
          return;
        }
        e.preventDefault();
        dropHandledRef.current = true;
        const fromId =
          draggingSectionId ?? e.dataTransfer.getData(SECTION_DRAG_TYPE);
        if (fromId) {
          onDrop(fromId, gapAt(e));
        }
        clear();
      },
    };
  }

  return { draggingSectionId, dropIndex, dragHandlers, dropHandlers };
}
